// Firestore access for supporter tiers + per-player spend.
//
// Two collections:
//   supporterTiers/{autoId}  — { name, minSpend, accent, glow }
//   playerSpend/{userId}     — { userId, totalSpend, note, updatedAt, updatedBy }
//
// Both are publicly readable; writes are gated on the `tournaments.manage`
// permission in firestore.rules.
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from './config';
import { DEFAULT_TIERS } from '../constants/supporterTiers';

const sortTiers = (tiers) => [...tiers].sort((a, b) => (a.minSpend || 0) - (b.minSpend || 0));

// ---- Tiers ----------------------------------------------------------------

export const getTiers = async () => {
  const snap = await getDocs(collection(db, 'supporterTiers'));
  return sortTiers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
};

export const listenTiers = (callback) =>
  onSnapshot(
    query(collection(db, 'supporterTiers')),
    (snap) => callback(sortTiers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))),
    () => callback([])
  );

const cleanTier = (tier) => ({
  name: (tier.name || '').trim(),
  minSpend: Number(tier.minSpend) || 0,
  accent: tier.accent || 'muted',
  glow: !!tier.glow,
});

export const addTier = async (tier) => {
  const ref = await addDoc(collection(db, 'supporterTiers'), {
    ...cleanTier(tier),
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const updateTier = async (id, tier) =>
  updateDoc(doc(db, 'supporterTiers', id), {
    ...cleanTier(tier),
    updatedAt: serverTimestamp(),
  });

export const deleteTier = async (id) => deleteDoc(doc(db, 'supporterTiers', id));

// Bootstrap the collection with the seed tiers. No-op if any tier already exists.
export const seedDefaultTiers = async () => {
  const existing = await getDocs(collection(db, 'supporterTiers'));
  if (!existing.empty) return false;
  const batch = writeBatch(db);
  DEFAULT_TIERS.forEach((t) => {
    batch.set(doc(collection(db, 'supporterTiers')), {
      ...cleanTier(t),
      createdAt: serverTimestamp(),
    });
  });
  await batch.commit();
  return true;
};

// ---- Player spend ---------------------------------------------------------

export const getPlayerSpend = async (userId) => {
  if (!userId) return 0;
  const snap = await getDoc(doc(db, 'playerSpend', userId));
  return snap.exists() ? Number(snap.data().totalSpend) || 0 : 0;
};

// Fetch every spend record at once, keyed by userId. The collection is small
// (one doc per backer), so a single read is cheaper than N per-user lookups.
export const getAllPlayerSpend = async () => {
  const snap = await getDocs(collection(db, 'playerSpend'));
  const map = {};
  snap.docs.forEach((d) => {
    map[d.id] = Number(d.data().totalSpend) || 0;
  });
  return map;
};

export const listenAllPlayerSpend = (callback) =>
  onSnapshot(
    collection(db, 'playerSpend'),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => callback([])
  );

export const setPlayerSpend = async (userId, totalSpend, { note = '', updatedBy = null } = {}) => {
  if (!userId) throw new Error('userId is required');
  await setDoc(
    doc(db, 'playerSpend', userId),
    {
      userId,
      totalSpend: Number(totalSpend) || 0,
      note: note || '',
      updatedAt: serverTimestamp(),
      updatedBy: updatedBy || null,
    },
    { merge: true }
  );
  return true;
};

export const deletePlayerSpend = async (userId) => deleteDoc(doc(db, 'playerSpend', userId));
